import { Reveal } from "@/components/Reveal";
import { SectionParticles } from "@/components/SectionParticles";
import { SectionBg } from "@/components/SectionBg";
import { PREVIEW_MODE } from "@/lib/campaign";
import { scrollToId } from "@/lib/scroll";
import { sendReplayEvent } from "@/lib/replayTracking";

const goBook = () => {
  sendReplayEvent("cta_clicked");
  scrollToId("book");
};

/* REPLAY SECTION 3 — WHAT YOU JUST LEARNED (id="recap") */
const RECAP_ROWS = [
  { t: "ELIGIBILITY & ENTITLEMENT", b: "Use the correct verification path; prior use does not answer the question by itself." },
  { t: "BUYING POWER", b: "Income, debts, credit, assets, reserves, occupancy, and property facts belong in an individual review—not a universal online number." },
  { t: "CASH CATEGORIES", b: "A possible no-down-payment path does not mean a transaction requires no cash; deposits, inspections, appraisal, closing costs, prepaid items, reserves, and a possible funding fee may matter." },
  { t: "PURCHASE PATH", b: "Preparation, property review, offer coordination, appraisal, documentation, and underwriting all affect the process; seller acceptance and closing are never promised." },
];

export const ReplayRecap = () => (
  <section id="recap" className="relative overflow-hidden px-5 md:px-10 lg:px-16 py-28 md:py-36" data-testid="replay-recap-section">
    <SectionBg img="bg-home-1.jpg" strength={0.92} />
    <SectionParticles variant="keys" />
    <div className="relative z-10 max-w-5xl mx-auto text-left">
      <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-10">[001] — WHAT YOU JUST LEARNED</p>
      <Reveal>
        <h2 className="font-display font-semibold text-3xl md:text-5xl leading-tight text-white mb-8">
          AN EARNED BENEFIT STILL NEEDS A VERIFIED, INDIVIDUAL PLAN.
        </h2>
        <p className="text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.78)] max-w-3xl mb-14">
          The workshop separated benefit education from an individual mortgage decision: verify eligibility and
          entitlement questions, model the inputs behind buying power and potential cash, then move only after
          the relevant borrower and property facts are reviewed.
        </p>
      </Reveal>
      <div className="border-t border-[var(--line)] mb-12">
        {RECAP_ROWS.map((r, i) => (
          <Reveal key={r.t} delay={i * 0.08}>
            <div className="grid grid-cols-1 md:grid-cols-[0.35fr_0.65fr] gap-2 md:gap-8 py-7 border-b border-[var(--line)]" data-testid={`recap-row-${i}`}>
              <p className="font-mono text-sm tracking-[0.18em] text-[var(--accent)]">{r.t}</p>
              <p className="text-sm md:text-base leading-relaxed text-[rgba(255,255,255,0.78)]">{r.b}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <button
        data-testid="recap-cta-button"
        onClick={goBook}
        className="glint border border-[var(--accent)] text-[var(--accent)] font-semibold text-sm tracking-[0.12em] px-8 py-4 rounded-[4px] hover:bg-[var(--accent)] hover:text-[var(--background-deep)] transition-colors duration-250"
      >
        ORGANIZE MY PRIVATE BENEFIT-TO-KEYS CONVERSATION →
      </button>
    </div>
  </section>
);

/* REPLAY SECTION 4 — THE METHOD (id="method") */
const STAGES = [
  { n: "01", t: "VERIFY THE BENEFIT", b: "Identify eligibility, entitlement, prior-use, funding-fee, and documentation questions that may require VA and lender verification." },
  { n: "02", t: "MODEL THE REAL INPUTS", b: "Organize the factors behind buying power, potential cash needed, credit readiness, occupancy, property, and relevant comparison scenarios. This is education—not a payment quote or approval." },
  { n: "03", t: "MOVE WITH A WRITTEN PLAN", b: "Choose whether to research eligibility, prepare, request a private session, apply, begin a home search, or wait with a plan after the relevant facts are reviewed." },
];

export const ReplayMethod = () => (
  <section id="method" className="relative overflow-hidden px-5 md:px-10 lg:px-16 py-28 md:py-36 hairline-t" data-testid="replay-method-section">
    <div aria-hidden="true" className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/assets/bg-home-2.jpg)" }} />
    <div aria-hidden="true" className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,99,0.96), rgba(0,0,99,0.9), rgba(0,0,99,0.96))" }} />
    <SectionParticles variant="houses" />
    <div className="relative z-10 max-w-6xl mx-auto text-left">
      <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-10">[002] — THE FRAMEWORK</p>
      <Reveal>
        <h2 className="font-display font-semibold text-3xl md:text-5xl text-white mb-4">
          THE FLORIDA VA BENEFIT-TO-KEYS BLUEPRINT™
        </h2>
        <p className="text-base md:text-lg text-[rgba(255,255,255,0.72)] mb-14">
          A three-stage framework for turning benefit questions into a written next-step map.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-3 lg:divide-x divide-[var(--line)] border-y border-[var(--line)]">
        {STAGES.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.1} className="relative p-8 md:p-10 group hover:bg-[var(--panel)] transition-colors duration-300">
            <span aria-hidden="true" className="font-display font-semibold text-[6.5rem] leading-none text-white/[0.05] group-hover:text-[rgba(148,222,82,0.12)] transition-colors duration-300 absolute top-4 right-4 select-none">
              {s.n}
            </span>
            <div className="relative pt-16">
              <h3 className="font-mono text-sm tracking-[0.2em] text-[var(--accent)] mb-4">{s.n} — {s.t}</h3>
              <p className="text-sm md:text-base leading-relaxed text-[rgba(255,255,255,0.75)]">{s.b}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* REPLAY SECTION 5 — THE COMPLIMENTARY SESSION */
const DELIVERABLES = [
  { t: "BENEFIT-TO-KEYS ACTION MAP", b: "A concise summary of the stated goal, current stage and timeline, biggest open questions, and next three actions. It is a planning document—not an eligibility determination or loan approval." },
  { t: "ELIGIBILITY, BUYING-POWER & CASH QUESTION PLANNER", b: "A structured list of benefit, entitlement, payment-input, funding-fee, and potential cash categories that may need estimates or verification. No universal eligibility, payment, no-down-payment, or cash-to-close result is promised." },
  { t: "VA HOMEBUYING READINESS REVIEW", b: "An educational conversation about the credit, income, assets, debts, documents, occupancy, property, and financing-path questions that may be relevant. If an application or credit review becomes appropriate, process and consent come first." },
];

export const ReplaySession = () => (
  <section className="relative overflow-hidden px-5 md:px-10 lg:px-16 py-28 md:py-36 hairline-t" data-testid="replay-session-section">
    <SectionBg img="bg-home-5.jpg" strength={0.92} />
    <SectionParticles variant="circles" />
    <div className="relative z-10 max-w-6xl mx-auto text-left">
      <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-10">[003] — YOUR OPTIONAL NEXT STEP</p>
      <Reveal>
        <h2 className="font-display font-semibold text-3xl md:text-5xl leading-tight text-white mb-8 max-w-4xl">
          TURN GENERAL VA EDUCATION INTO YOUR BENEFIT-TO-KEYS ACTION MAP.
        </h2>
        <p className="text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.78)] max-w-3xl mb-14">
          A private conversation with Chaunci or her team to organize the stated housing goal, the biggest
          eligibility and financing questions, the information that may need review, and the next responsible
          action. There is no obligation to apply.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {DELIVERABLES.map((d, i) => (
          <Reveal key={d.t} delay={i * 0.08}>
            <div className="h-full glass-panel rounded-[6px] p-7 hover:border-[rgba(148,222,82,0.4)] transition-colors duration-300" data-testid={`deliverable-card-${i}`}>
              <p className="font-mono text-xs tracking-[0.14em] text-[var(--accent)] mb-4">{d.t}</p>
              <p className="text-sm leading-relaxed text-[rgba(255,255,255,0.78)]">{d.b}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="font-mono text-[11px] tracking-[0.18em] text-[var(--muted)] mb-10">
        Private · Educational · No obligation to apply
      </p>
      <button
        data-testid="session-cta-button"
        onClick={goBook}
        className="glint bg-[var(--accent)] text-[var(--background-deep)] font-semibold text-sm tracking-[0.12em] px-8 py-4 rounded-[4px] hover:brightness-110 hover:-translate-y-[1px] transition-[transform,filter] duration-200"
      >
        REQUEST MY COMPLIMENTARY BENEFIT-TO-KEYS SESSION
      </button>
    </div>
  </section>
);

/* REPLAY SECTION 6 — CLEAR NEXT STEPS PROMISE */
export const ReplayPromise = () => (
  <section className="relative overflow-hidden px-5 md:px-10 lg:px-16 py-28 md:py-36 hairline-t" data-testid="replay-promise-section">
    <SectionBg img="bg-home-6.jpg" strength={0.93} />
    <SectionParticles variant="keys" count={16} />
    <div className="relative z-10 max-w-3xl mx-auto">
      <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-10 text-left">[004] — CLARITY PROMISE</p>
      <Reveal>
        <div className="border border-[var(--accent)] rounded-[6px] p-8 md:p-12 text-center" data-testid="clarity-promise-card">
          <p className="font-display font-semibold text-2xl md:text-3xl text-[var(--accent)] mb-6">
            THE VA CLEAR NEXT STEPS PROMISE™
          </p>
          <p className="text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.85)] mb-8">
            Complete the workshop and the Benefit-to-Keys Workbook. If you still cannot name three next
            actions, Chaunci's team will provide a complimentary clarification review and help finish the map.
          </p>
          <p className="text-[11px] leading-relaxed text-[var(--muted)]">
            This promise covers clarity and service delivery only. It does not guarantee VA eligibility or
            entitlement, approval, pre-approval, a rate or payment, savings, no down payment, no cash needed at
            closing, seller acceptance, appraisal, property condition, underwriting, or closing.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* REPLAY SECTION 8 — OBJECTIONS / EMPATHY */
const SESSION_STEPS = [
  "We organize the eligibility, entitlement, prior-use, and documentation questions.",
  "We separate general benefit education from the inputs that require individual review.",
  "You leave with next actions whether the responsible timing is now, later, or after preparation.",
];

export const ReplayObjections = () => (
  <section className="relative overflow-hidden px-5 md:px-10 lg:px-16 py-28 md:py-36 hairline-t" data-testid="replay-objections-section">
    <div aria-hidden="true" className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/assets/bg-home-3.jpg)" }} />
    <div aria-hidden="true" className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,99,0.95), rgba(0,0,99,0.89), rgba(0,0,99,0.95))" }} />
    <SectionParticles variant="circles" />
    <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 text-left">
      <div>
        <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-10">[006] — STILL DECIDING?</p>
        <Reveal>
          <h2 className="font-display font-semibold text-3xl md:text-4xl leading-tight text-white mb-8">
            YOU DO NOT NEED EVERY VA ANSWER TODAY. YOU NEED THE RIGHT QUESTIONS IN THE RIGHT ORDER.
          </h2>
        </Reveal>
        <Reveal>
          <div className="space-y-5 text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.78)]">
            <p>
              Wondering, "What if I used VA financing before?" That is one of the most common questions in the
              room—and it is exactly the kind of question that has a verification path rather than a guess.
              Prior use raises follow-up questions about remaining entitlement, and those questions have an
              orderly way to be asked and answered. A session simply puts them in order.
            </p>
            <p>
              And if you are not ready to apply, that is a completely reasonable place to be. The session is
              not an application and carries no obligation to become one. Some conversations end with
              preparation steps, some with research items, and some with a decision to wait—each with a written
              map, so the next move is yours to choose.
            </p>
          </div>
        </Reveal>
      </div>
      <div className="lg:pt-20">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.24em] text-[var(--accent)] mb-8">
            HERE IS WHAT HAPPENS WHEN YOU REQUEST A SESSION:
          </p>
        </Reveal>
        <div className="space-y-6">
          {SESSION_STEPS.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="flex items-start gap-4 border-b border-[var(--line)] pb-6" data-testid={`session-step-${i}`}>
                <span aria-hidden="true" className="font-mono text-[var(--accent)] shrink-0">→</span>
                <p className="text-sm md:text-base leading-relaxed text-[rgba(255,255,255,0.82)]">{s}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* REPLAY SECTION 9 — VERIFIED PROOF (preview only; omitted entirely in production) */
const PROOF_PLACEHOLDER =
  "VERIFIED KINSFOLK / CHAUNCI REVIEW — INSERT ONLY AFTER SOURCE URL, PERMISSION, EXACT QUOTE, DATE, AND COMPLIANCE APPROVAL.";

export const ReplayProof = () => {
  if (!PREVIEW_MODE) return null;
  return (
    <section className="px-5 md:px-10 lg:px-16 py-28 md:py-36 hairline-t" data-testid="replay-proof-section">
      <div className="max-w-6xl mx-auto text-left">
        <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-12">[007] — CLIENT EXPERIENCE</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-[6px] border border-dashed border-[rgba(255,255,255,0.25)] p-7" data-testid={`replay-proof-placeholder-${i}`}>
              <p className="font-mono text-[10px] leading-relaxed tracking-[0.06em] text-[var(--muted)] uppercase">
                {PROOF_PLACEHOLDER}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
